/**
 * Ably Spaces Service
 *
 * Provides real-time collaboration features for course editing:
 * - Component locking (prevent edit conflicts)
 * - Live cursors (see where others are working)
 * - Presence (see who's online)
 */
import { isPlatformBrowser } from '@angular/common';
import { Injectable, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject, catchError, map, of } from 'rxjs';
import { HttpService } from '../../services/http/http.service';
import {
  AblyTokenResponse,
  Collaborator,
  CursorPosition,
  DragEvent,
  DragEventItem,
  DragEventType,
  LockInfo,
  UserLocation,
  VideoUploadEvent
} from '../models/collaboration.models';

// Re-export for backward compatibility
export {
  AblyTokenResponse, Collaborator,
  CursorPosition, DragEvent,
  DragEventItem,
  DragEventType, LockInfo, UserLocation, VideoUploadEvent
};

@Injectable({
  providedIn: 'root',
})
export class AblySpacesService implements OnDestroy {
  private http = inject(HttpService);
  private platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  // Ably instances (dynamically imported)
  private Realtime: any;
  private Spaces: any;
  private client: any;
  private spaces: any;
  private space: any;
  private modulesPreloaded = false;
  private modulesPreloading: Promise<void> | null = null;

  // State
  private currentUser: AblyTokenResponse['user'] | null = null;
  private currentProjectId: string | null = null;
  private currentSpaceName: string | null = null; // Store space name for channel access
  private isConnected = false;
  private isConnecting = false;
  private beforeUnloadHandler: (() => void) | null = null;
  private visibilityChangeHandler: (() => void) | null = null;
  private currentLocation: UserLocation | null = null;
  private pendingLocks = new Set<string>(); // Track pending lock requests

  // Cost optimization: Debounce location updates
  private pendingLocationUpdate: { location: UserLocation; timeoutId: any } | null = null;
  private locationDebounceMs = 500; // Wait 500ms before sending location update
  private lastPublishedLocation: string | null = null; // Track last location to avoid duplicates

  // Cost optimization: Tab visibility management
  // Only pauses cursor SENDING when hidden - connection stays alive to RECEIVE updates
  private isTabVisible = true;
  private cursorsPaused = false;

  // Reconnection management: Prevent multiple simultaneous reconnection attempts
  private isReconnecting = false;
  private reconnectionDebounceMs = 2000; // Minimum time between reconnection attempts
  private lastReconnectionAttempt = 0;

  // Cost optimization: Disconnect Ably after extended idle period
  // NOTE: This only applies to Ably (which costs per connection minute).
  // SSE stays connected always to keep the store updated - SSE is essentially free.
  private idleDisconnectTimeoutId: any = null;
  private idleDisconnectMs = 60 * 60 * 1000; // 60 minutes of hidden tab = disconnect Ably
  private wasIdleDisconnected = false;

  // Observable state
  private collaboratorsSubject = new BehaviorSubject<Collaborator[]>([]);
  private locksSubject = new BehaviorSubject<Map<string, LockInfo>>(new Map());
  private connectionStatusSubject = new BehaviorSubject<'disconnected' | 'connecting' | 'connected'>('disconnected');
  private currentLocationSubject = new BehaviorSubject<UserLocation | null>(null);
  private cursorsPausedSubject = new BehaviorSubject<boolean>(false);
  private tabBecameVisibleSubject = new Subject<boolean>();

  collaborators$ = this.collaboratorsSubject.asObservable();
  locks$ = this.locksSubject.asObservable();
  connectionStatus$ = this.connectionStatusSubject.asObservable();
  currentLocation$ = this.currentLocationSubject.asObservable();
  cursorsPaused$ = this.cursorsPausedSubject.asObservable(); // Components can subscribe to pause cursor sending
  tabBecameVisible$ = this.tabBecameVisibleSubject.asObservable(); // Components can refresh data when tab becomes visible

  // ============================================================================
  // VIDEO UPLOAD EVENTS VIA ABLY - DISABLED (using SSE instead for cost savings)
  // ============================================================================
  // To enable Ably for video upload events instead of SSE:
  // 1. Uncomment videoUploadEventSubject and videoUploadEvents$ below
  // 2. Uncomment subscribeToVideoUploadEvents() call in connect() method
  // 3. Uncomment the subscribeToVideoUploadEvents() and unsubscribeFromVideoUploadEvents() methods
  // 4. Update CollaborationFacade to expose videoUploadEvents$
  // 5. Update course-detail.component.ts to subscribe to collaboration.videoUploadEvents$
  //
  // Benefits of Ably: Sub-second latency, more reliable for real-time
  // Benefits of SSE: No extra cost, already connected for project events
  // ============================================================================
  // private videoUploadEventSubject = new Subject<VideoUploadEvent>();
  // videoUploadEvents$ = this.videoUploadEventSubject.asObservable();

  /**
   * Preload Ably modules to speed up connection
   * Call this early (e.g., when entering instructor area) to warm up the import cache
   * This eliminates the ~500-1000ms dynamic import delay during actual connection
   */
  async preloadModules(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // If already preloaded or preloading, return existing promise or resolve immediately
    if (this.modulesPreloaded) {
      return;
    }

    if (this.modulesPreloading) {
      return this.modulesPreloading;
    }

    console.log('AblySpacesService: Preloading Ably modules...');
    const startTime = Date.now();

    this.modulesPreloading = (async () => {
      try {
        // @ts-ignore - Dynamic imports for browser-only modules
        const ablyModule = await import('ably');
        // @ts-ignore - Dynamic imports for browser-only modules
        const spacesModule = await import('@ably/spaces');

        // Handle different module formats (ESM vs CommonJS interop)
        // In ESM: ablyModule.Realtime exists directly
        // In CJS interop (some bundlers like Cloudflare): ablyModule.default may contain exports
        this.Realtime = ablyModule.Realtime ?? (ablyModule as any).default?.Realtime ?? (ablyModule as any).default;
        this.Spaces = spacesModule.default ?? (spacesModule as any).Spaces ?? spacesModule;

        if (!this.Realtime) {
          throw new Error('Failed to resolve Ably Realtime constructor from module');
        }
        if (!this.Spaces) {
          throw new Error('Failed to resolve Ably Spaces constructor from module');
        }

        this.modulesPreloaded = true;

        console.log(`AblySpacesService: Modules preloaded in ${Date.now() - startTime}ms`);
      } catch (error) {
        console.warn('AblySpacesService: Failed to preload modules', error);
        // Don't throw - allow connection to try importing again
      } finally {
        this.modulesPreloading = null;
      }
    })();

    return this.modulesPreloading;
  }

  /**
   * Connect to Ably Spaces for a specific project
   * @param projectId - The project ID to connect to
   * @param preloadedToken - Optional pre-fetched token from permissions API (faster connection)
   */
  async connect(projectId: string, preloadedToken?: AblyTokenResponse | null): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('AblySpacesService: Not running in browser');
      return false;
    }

    // Already connected to this project - just return success
    if (this.isConnected && this.currentProjectId === projectId) {
      console.log('AblySpacesService: Already connected to this project');
      return true;
    }

    // Connection in progress for this project - wait for it
    if (this.isConnecting && this.currentProjectId === projectId) {
      console.log('AblySpacesService: Connection already in progress for this project');
      // Wait for connection to complete
      return new Promise((resolve) => {
        const checkConnection = setInterval(() => {
          if (!this.isConnecting) {
            clearInterval(checkConnection);
            resolve(this.isConnected);
          }
        }, 100);
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkConnection);
          resolve(this.isConnected);
        }, 10000);
      });
    }

    // Disconnect from previous project if any (different project)
    if (this.isConnected && this.currentProjectId !== projectId) {
      await this.disconnect();
    }

    this.isConnecting = true;
    this.connectionStatusSubject.next('connecting');
    this.currentProjectId = projectId;

    try {
      console.log('AblySpacesService: Starting connection...');
      const connectionStartTime = Date.now();

      // Use pre-fetched token if provided, otherwise fetch from API
      let tokenResponse: AblyTokenResponse | null | undefined;
      if (preloadedToken) {
        console.log('AblySpacesService: Using pre-fetched token (faster)');
        tokenResponse = preloadedToken;
        // Still need to ensure modules are loaded
        if (!this.modulesPreloaded) {
          await this.preloadModules();
        }
      } else {
        // Run token fetch and module loading in parallel for faster connection
        [tokenResponse] = await Promise.all([
          this.getToken(projectId).toPromise(),
          // Only load modules if not already preloaded
          this.modulesPreloaded ? Promise.resolve() : this.preloadModules(),
        ]);
      }

      if (!tokenResponse) {
        throw new Error('Failed to get Ably token');
      }

      console.log(`AblySpacesService: Token + modules ready in ${Date.now() - connectionStartTime}ms`);

      this.currentUser = tokenResponse.user;

      // Create Ably client with token from backend
      // Backend issues full tokens so client doesn't need to call Ably REST API
      // Note: The "WebSocket closed before connection established" warning on first attempt
      // is normal - Ably SDK handles retries internally and will connect successfully.
      this.client = new this.Realtime({
        token: tokenResponse.token.token,
        authCallback: async (tokenParams: any, callback: any) => {
          // Called when token needs renewal
          try {
            const response = await this.getToken(projectId).toPromise();
            callback(null, response?.token);
          } catch (err) {
            callback(err, null);
          }
        },
        // Connection options - let Ably handle retries
        disconnectedRetryTimeout: 1000,
        suspendedRetryTimeout: 2000,
      });

      // Create Spaces instance
      this.spaces = new this.Spaces(this.client);
      this.currentSpaceName = tokenResponse.space_name;
      this.space = await this.spaces.get(tokenResponse.space_name);

      // Enter the space with user profile (including id for lock restoration)
      await this.space.enter({
        id: String(this.currentUser.id),
        name: this.currentUser.name,
        email: this.currentUser.email,
        color: this.currentUser.color,
        avatar: this.currentUser.avatar,
        isOwner: this.currentUser.is_owner,
      });

      // Set up subscriptions - await async ones to prevent race conditions
      await this.setupMemberSubscriptions();
      await this.setupLockSubscriptions();
      this.setupCursorSubscriptions(); // Cursors don't have getAll(), no race condition
      await this.setupLocationSubscriptions();
      // Video upload events disabled - using SSE instead (see comments at top of class)
      // To enable: uncomment the line below and the related methods
      // this.subscribeToVideoUploadEvents();

      // Set up connection state monitoring for reconnection handling
      this.setupConnectionStateHandler();

      // Set up beforeunload handler to properly leave space on page refresh/close
      if (this.isBrowser) {
        this.beforeUnloadHandler = () => {
          // Use synchronous leave to ensure cleanup happens before page unloads
          if (this.space) {
            this.space.leave();
          }
          if (this.client) {
            this.client.close();
          }
        };
        window.addEventListener('beforeunload', this.beforeUnloadHandler);

        // Set up visibility change handler for cost optimization
        this.setupVisibilityChangeHandler(projectId);
      }

      this.isConnected = true;
      this.isConnecting = false;
      this.connectionStatusSubject.next('connected');

      console.log('AblySpacesService: Connected to space', tokenResponse.space_name);
      return true;
    } catch (error) {
      console.error('AblySpacesService: Connection failed', error);
      this.isConnecting = false;
      this.connectionStatusSubject.next('disconnected');
      return false;
    }
  }

  /**
   * Set up connection state monitoring for reconnection handling
   *
   * Ably SDK automatically handles reconnection, but Spaces SDK requires
   * manual re-entry via space.enter() after reconnection.
   *
   * References:
   * - https://ably.com/docs/connect/states
   * - Error 101001: Not entered space
   */
  private setupConnectionStateHandler(): void {
    if (!this.client) return;

    this.client.connection.on((stateChange: any) => {
      console.log('AblySpacesService: Connection state changed', stateChange.current, stateChange.previous);

      switch (stateChange.current) {
        case 'connected':
          // Reconnected after disconnection
          if (stateChange.previous === 'disconnected' ||
              stateChange.previous === 'suspended' ||
              stateChange.previous === 'connecting') {
            console.log('AblySpacesService: Reconnected, re-entering space...');
            this.handleReconnection();
          }
          break;

        case 'disconnected':
        case 'suspended':
          console.log('AblySpacesService: Connection lost, marking as connecting');
          // Don't fully disconnect, just mark state
          this.connectionStatusSubject.next('connecting');
          break;

        case 'failed':
          console.error('AblySpacesService: Connection failed permanently');
          this.connectionStatusSubject.next('disconnected');
          break;
      }
    });
  }

  /**
   * Handle reconnection after network interruption
   *
   * Re-enters the space and restores location state.
   * Called automatically when connection state changes to 'connected'
   * after a disconnection.
   *
   * IMPORTANT: This method is debounced to prevent multiple simultaneous
   * reconnection attempts when multiple operations (cursor, location, etc.)
   * all fail at the same time due to network issues.
   */
  private async handleReconnection(): Promise<void> {
    // Debounce: Prevent multiple simultaneous reconnection attempts
    const now = Date.now();
    if (this.isReconnecting) {
      console.log('AblySpacesService: Reconnection already in progress, skipping');
      return;
    }
    if (now - this.lastReconnectionAttempt < this.reconnectionDebounceMs) {
      console.log('AblySpacesService: Reconnection attempt too soon, skipping');
      return;
    }

    if (!this.space || !this.currentUser) {
      console.warn('AblySpacesService: Cannot reconnect - no space or user');
      return;
    }

    this.isReconnecting = true;
    this.lastReconnectionAttempt = now;
    console.log('AblySpacesService: Starting reconnection...');

    try {
      // Re-enter the space with user profile
      await this.space.enter({
        id: String(this.currentUser.id),
        name: this.currentUser.name,
        email: this.currentUser.email,
        color: this.currentUser.color,
        avatar: this.currentUser.avatar,
        isOwner: this.currentUser.is_owner,
      });

      console.log('AblySpacesService: Successfully re-entered space');

      // Wait for space channels to be ready (attached)
      // This prevents error 90001: "channel not currently attached"
      await this.waitForSpaceChannelsReady();

      // Clear stale lock state and refresh from Ably
      await this.refreshLockStateAfterReconnection();

      // Clear pending locks to prevent stuck state
      this.pendingLocks.clear();

      this.connectionStatusSubject.next('connected');
      this.isReconnecting = false;

      // Restore location if we had one
      if (this.currentLocation) {
        console.log('AblySpacesService: Restoring location', this.currentLocation);
        try {
          await this.space.locations.set(this.currentLocation);
        } catch (locError) {
          console.warn('AblySpacesService: Failed to restore location', locError);
        }
      }

    } catch (error) {
      console.error('AblySpacesService: Failed to re-enter space', error);
      this.isReconnecting = false;
    }
  }

  /**
   * Refresh lock state after reconnection
   *
   * Clears stale lock state and fetches current locks from Ably.
   * This ensures locks don't get stuck after network interruption.
   */
  private async refreshLockStateAfterReconnection(): Promise<void> {
    if (!this.space) return;

    try {
      console.log('AblySpacesService: Refreshing lock state after reconnection');

      // Get current locks from Ably
      const locks = await this.space.locks.getAll();

      // Build new lock state map
      const newLockState = new Map<string, LockInfo>();
      const currentUserId = String(this.currentUser?.id);

      for (const lock of locks) {
        if (lock.status === 'locked') {
          const memberId = String(lock.member?.profileData?.id || lock.member?.clientId);
          const isLockedByMe = memberId === currentUserId;

          newLockState.set(lock.id, {
            componentId: lock.id,
            isLockedByMe,
            memberId,
            memberName: lock.member?.profileData?.name || 'Unknown',
            memberColor: lock.member?.profileData?.color || '#3b82f6',
            lockedAt: new Date(lock.timestamp || Date.now()),
          });
        }
      }

      // Update lock state
      this.locksSubject.next(newLockState);
      console.log('AblySpacesService: Lock state refreshed, active locks:', newLockState.size);

    } catch (error) {
      console.error('AblySpacesService: Failed to refresh lock state', error);
      // On error, clear all locks to prevent stuck state
      this.locksSubject.next(new Map());
    }
  }

  /**
   * Wait for space channels to be attached and ready
   * Prevents error 90001: "Unable to perform operation on channel (not currently attached)"
   *
   * Reference: https://faqs.ably.com/error-code-90001-channel-operation-failed-invalid-channel-state
   */
  private async waitForSpaceChannelsReady(timeoutMs: number = 5000): Promise<void> {
    if (!this.client || !this.currentSpaceName) {
      return;
    }

    try {
      // Get the space channel (used for locks, cursors, locations, etc.)
      const spaceChannel = this.client.channels.get(`${this.currentSpaceName}::$space`);

      // If already attached, we're good
      if (spaceChannel.state === 'attached') {
        console.log('AblySpacesService: Space channel already attached');
        return;
      }

      console.log('AblySpacesService: Waiting for space channel to attach...', spaceChannel.state);

      // Wait for channel to become attached
      await new Promise<void>((resolve, reject) => {
        const stateListener = (stateChange: any) => {
          if (stateChange.current === 'attached') {
            clearTimeout(timeout);
            spaceChannel.off('attached', stateListener);
            console.log('AblySpacesService: Space channel attached successfully');
            resolve();
          } else if (stateChange.current === 'failed') {
            clearTimeout(timeout);
            spaceChannel.off('attached', stateListener);
            reject(new Error('Channel attach failed'));
          }
        };

        const timeout = setTimeout(() => {
          spaceChannel.off('attached', stateListener); // Clean up listener on timeout
          reject(new Error('Channel attach timeout'));
        }, timeoutMs);

        spaceChannel.on('attached', stateListener);

        // Race condition: channel became attached between initial check and listener registration
        if (spaceChannel.state === 'attached') {
          clearTimeout(timeout);
          spaceChannel.off('attached', stateListener); // Clean up listener before resolving
          resolve();
        }
      });
    } catch (error) {
      console.warn('AblySpacesService: Failed to wait for channel ready', error);
      // Don't throw - allow operations to proceed and fail gracefully
    }
  }

  /**
   * Check if we're properly connected and in the space
   * @returns true if ready for operations, false otherwise
   * Note: Returns false silently when Ably is not connected - this is expected
   * when has_collaborators is false or Ably is disabled
   */
  private checkSpaceReady(): boolean {
    if (!this.space || !this.isConnected) {
      // Silent return - Ably may be intentionally skipped (no collaborators, disabled, etc.)
      return false;
    }

    // Don't allow operations while reconnecting to prevent loops
    if (this.isReconnecting) {
      return false;
    }

    return true;
  }

  /**
   * Disconnect from Ably Spaces
   */
  async disconnect(): Promise<void> {
    if (!this.isConnected && !this.isConnecting) return;

    console.log('AblySpacesService: Disconnecting...');
    this.isConnecting = false;

    // Remove connection state listener
    if (this.client?.connection) {
      try {
        this.client.connection.off();
      } catch (e) {
        // Ignore errors during cleanup
      }
    }

    // Remove beforeunload handler
    if (this.isBrowser && this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }

    // Clean up visibility handler
    this.cleanupVisibilityHandler();

    // Mark as disconnected immediately to prevent race conditions
    this.isConnected = false;
    const spaceToLeave = this.space;
    const clientToClose = this.client;
    const fieldChannelToDetach = this.fieldValuesChannel;
    const listChannelToDetach = this.listChangesChannel;
    const dragEventsChannelToDetach = this.dragEventsChannel;
    // Video upload via Ably disabled - using SSE instead
    // const videoUploadChannelToDetach = this.videoUploadChannel;

    // Clear references immediately
    this.space = null;
    this.spaces = null;
    this.client = null;
    this.currentUser = null;
    this.currentProjectId = null;
    this.currentSpaceName = null;
    this.currentLocation = null;

    // Clear all pending locks to prevent stale state
    if (this.pendingLocks.size > 0) {
      console.log('AblySpacesService: Clearing', this.pendingLocks.size, 'pending locks on disconnect');
      this.pendingLocks.clear();
    }

    // Reset reconnection state
    this.isReconnecting = false;
    this.lastReconnectionAttempt = 0;

    // Clear pending location update
    if (this.pendingLocationUpdate) {
      clearTimeout(this.pendingLocationUpdate.timeoutId);
      this.pendingLocationUpdate = null;
    }
    this.lastPublishedLocation = null;
    this.fieldValuesChannel = null;
    this.listChangesChannel = null;
    this.dragEventsChannel = null;
    // this.videoUploadChannel = null; // Video upload via Ably disabled
    // Clear pending field updates and last published values
    this.pendingFieldUpdates.forEach(({ timeoutId }) => clearTimeout(timeoutId));
    this.pendingFieldUpdates.clear();
    this.lastPublishedValues.clear();
    this.collaboratorsSubject.next([]);
    this.locksSubject.next(new Map());
    this.connectionStatusSubject.next('disconnected');
    this.currentLocationSubject.next(null);

    // Then do async cleanup
    try {
      // Unsubscribe and detach field values channel first
      if (fieldChannelToDetach) {
        try {
          fieldChannelToDetach.unsubscribe();
          await fieldChannelToDetach.detach();
          console.log('AblySpacesService: Detached field values channel');
        } catch (e) {
          // Ignore detach errors
        }
      }

      // Unsubscribe and detach list changes channel
      if (listChannelToDetach) {
        try {
          listChannelToDetach.unsubscribe();
          await listChannelToDetach.detach();
          console.log('AblySpacesService: Detached list changes channel');
        } catch (e) {
          // Ignore detach errors
        }
      }

      // Unsubscribe and detach drag events channel
      if (dragEventsChannelToDetach) {
        try {
          dragEventsChannelToDetach.unsubscribe();
          await dragEventsChannelToDetach.detach();
          console.log('AblySpacesService: Detached drag events channel');
        } catch (e) {
          // Ignore detach errors
        }
      }

      // Video upload via Ably disabled - using SSE instead
      // Uncomment below to re-enable Ably video upload events
      // if (videoUploadChannelToDetach) {
      //   try {
      //     videoUploadChannelToDetach.unsubscribe();
      //     console.log('AblySpacesService: Unsubscribed from video upload channel');
      //   } catch (e) {
      //     // Ignore unsubscribe errors
      //   }
      // }

      if (spaceToLeave) {
        // Unsubscribe from all events first
        try {
          spaceToLeave.members.unsubscribe();
          spaceToLeave.locks.unsubscribe();
          spaceToLeave.cursors.unsubscribe();
          spaceToLeave.locations.unsubscribe();
        } catch (e) {
          // Ignore unsubscribe errors
        }

        await spaceToLeave.leave();
        console.log('AblySpacesService: Left space');

        // Give Ably time to process the leave before closing
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      if (clientToClose) {
        clientToClose.close();
        console.log('AblySpacesService: Closed client');
      }
    } catch (error) {
      console.error('AblySpacesService: Disconnect error', error);
    }
  }

  /**
   * Acquire a lock on a component
   * Returns true if lock acquired OR if Ably not connected (to not block editing)
   */
  async acquireLock(componentId: string): Promise<boolean> {
    if (!this.checkSpaceReady()) {
      // Allow editing when Ably is not connected - SSE still works for updates
      return true;
    }

    // Check if we already own this lock
    const existingLock = this.locksSubject.value.get(componentId);
    if (existingLock?.isLockedByMe) {
      console.log('AblySpacesService: Already own this lock', componentId);
      return true;
    }

    // Check if we have a pending request - but verify it's actually pending
    if (this.pendingLocks.has(componentId)) {
      // Verify the lock is actually in a pending state, not stale
      const lockState = this.locksSubject.value.get(componentId);
      if (!lockState || lockState.isLockedByMe) {
        // Stale pending state - clear it and proceed
        console.log('AblySpacesService: Clearing stale pending lock state', componentId);
        this.pendingLocks.delete(componentId);
      } else {
        console.log('AblySpacesService: Lock request already pending for', componentId, '- waiting');
        // Wait for the existing pending request
        const confirmed = await this.waitForLockConfirmation(componentId, 3000);
        return confirmed;
      }
    }

    try {
      this.pendingLocks.add(componentId);

      // Auto-cleanup after 5 seconds to prevent stuck state
      const cleanupTimeout = setTimeout(() => {
        if (this.pendingLocks.has(componentId)) {
          console.warn('AblySpacesService: Auto-cleaning stale pending lock', componentId);
          this.pendingLocks.delete(componentId);
        }
      }, 5000);

      const lock = await this.space.locks.acquire(componentId);
      clearTimeout(cleanupTimeout);
      console.log('AblySpacesService: Lock acquire initial response', { componentId, status: lock?.status });

      // If lock is pending, wait for it to be confirmed
      if (lock?.status === 'pending') {
        // Wait for lock status to change (with timeout)
        const confirmed = await this.waitForLockConfirmation(componentId, 3000);
        this.pendingLocks.delete(componentId);
        console.log('AblySpacesService: Lock confirmation result', { componentId, confirmed });
        return confirmed;
      }

      this.pendingLocks.delete(componentId);
      const acquired = lock?.status === 'locked';
      if (!acquired) {
        console.log('AblySpacesService: Lock not acquired, status:', lock?.status);
      }
      return acquired;
    } catch (error: any) {
      // Always clean up pending state on error
      this.pendingLocks.delete(componentId);

      // Handle "lock request already exists" error - wait for existing request
      if (error?.code === 101004) {
        console.log('AblySpacesService: Lock request already exists for', componentId, '- waiting for confirmation');
        // Wait for the existing request to complete
        const confirmed = await this.waitForLockConfirmation(componentId, 3000);
        console.log('AblySpacesService: Existing lock request result', { componentId, confirmed });
        return confirmed;
      }

      // Handle "channel not attached" error (90001) - wait and retry once
      if (error?.code === 90001 || error?.message?.includes('not currently attached')) {
        console.warn('AblySpacesService: Channel not attached, waiting for channel ready...', error);
        try {
          await this.waitForSpaceChannelsReady(3000);
          console.log('AblySpacesService: Retrying lock acquisition after channel ready');
          // Retry once
          this.pendingLocks.add(componentId);

          // Auto-cleanup for retry
          const retryCleanupTimeout = setTimeout(() => {
            if (this.pendingLocks.has(componentId)) {
              console.warn('AblySpacesService: Auto-cleaning stale pending lock (retry)', componentId);
              this.pendingLocks.delete(componentId);
            }
          }, 5000);

          const lock = await this.space.locks.acquire(componentId);
          clearTimeout(retryCleanupTimeout);
          this.pendingLocks.delete(componentId);

          if (lock?.status === 'pending') {
            const confirmed = await this.waitForLockConfirmation(componentId, 3000);
            return confirmed;
          }

          return lock?.status === 'locked';
        } catch (retryError) {
          // Ensure cleanup on retry error
          this.pendingLocks.delete(componentId);
          console.error('AblySpacesService: Retry failed', retryError);
          return false;
        }
      }

      console.error('AblySpacesService: Failed to acquire lock', error);
      return false;
    }
  }

  /**
   * Wait for a lock to be confirmed (transition from pending to locked)
   */
  private waitForLockConfirmation(componentId: string, timeoutMs: number): Promise<boolean> {
    return new Promise((resolve) => {
      const startTime = Date.now();

      // Check immediately if lock is already confirmed in our state
      const existingLock = this.locksSubject.value.get(componentId);
      if (existingLock?.isLockedByMe) {
        resolve(true);
        return;
      }

      // Set up a subscription to lock updates
      const checkLock = async () => {
        const lock = this.locksSubject.value.get(componentId);
        if (lock?.isLockedByMe) {
          clearInterval(intervalId);
          resolve(true);
          return true;
        }
        if (Date.now() - startTime > timeoutMs) {
          clearInterval(intervalId);
          // Try to release the pending lock request on timeout
          try {
            await this.space?.locks.release(componentId);
            console.log('AblySpacesService: Released pending lock on timeout', componentId);
          } catch (e) {
            // Ignore release errors
          }
          resolve(false);
          return true;
        }
        return false;
      };

      // Poll for lock status changes
      const intervalId = setInterval(() => {
        checkLock();
      }, 50);

      // Also check immediately
      checkLock();
    });
  }

  /**
   * Release a lock on a component
   */
  async releaseLock(componentId: string): Promise<void> {
    if (!this.space) return;

    try {
      await this.space.locks.release(componentId);
    } catch (error: any) {
      // Handle "channel not attached" error (90001) - wait and retry once
      if (error?.code === 90001 || error?.message?.includes('not currently attached')) {
        console.warn('AblySpacesService: Channel not attached during release, retrying...', error);
        try {
          await this.waitForSpaceChannelsReady(3000);
          await this.space.locks.release(componentId);
        } catch (retryError) {
          console.error('AblySpacesService: Retry release failed', retryError);
        }
      } else {
        console.error('AblySpacesService: Failed to release lock', error);
      }
    }
  }

  /**
   * Check if a component is locked
   */
  isLocked(componentId: string): boolean {
    return this.locksSubject.value.has(componentId);
  }

  /**
   * Check if a component is locked by the current user
   */
  isLockedByMe(componentId: string): boolean {
    const lock = this.locksSubject.value.get(componentId);
    return lock?.isLockedByMe ?? false;
  }

  /**
   * Get lock info for a component
   */
  getLockInfo(componentId: string): LockInfo | undefined {
    return this.locksSubject.value.get(componentId);
  }

  /**
   * Update cursor position with optional scope
   * Scope identifies which cursor container these coordinates belong to.
   * Cursors are only displayed within matching scopes.
   */
  async updateCursorPosition(position: CursorPosition): Promise<void> {
    if (!this.checkSpaceReady()) {
      return;
    }

    try {
      await this.space.cursors.set({
        position: { x: position.x, y: position.y },
        data: {
          elementId: position.elementId,
          scope: position.scope, // Include scope for context-aware cursor display
        },
      });
    } catch (error: any) {
      // Handle "not entered space" errors - attempt reconnection
      if (error?.message?.includes('must enter a space') || error?.code === 101001) {
        console.warn('AblySpacesService: Cursor update failed, attempting to re-enter space');
        this.handleReconnection();
        return;
      }
      console.error('AblySpacesService: Failed to update cursor', error);
    }
  }

  /**
   * Clear cursor (e.g., when leaving an area)
   */
  async clearCursor(): Promise<void> {
    if (!this.space || !this.isConnected) return;

    try {
      await this.space.cursors.set({
        position: { x: 0, y: 0 },
        data: { state: 'hidden' },
      });
    } catch (error: any) {
      // Ignore "not entered space" errors
      if (error?.message?.includes('must enter a space')) {
        return;
      }
      console.error('AblySpacesService: Failed to clear cursor', error);
    }
  }

  /**
   * Set current location (page, drawer, etc.)
   * This is used to filter cursors - only show cursors from users on the same page
   * Cost optimization: Debounced with duplicate detection to reduce presence events
   */
  async setLocation(location: UserLocation): Promise<void> {
    // Always update local state immediately for responsive UI
    this.currentLocation = location;
    this.currentLocationSubject.next(location);

    if (!this.space) {
      // Store location for when we connect
      console.log('AblySpacesService: Location stored, will set when connected');
      return;
    }

    // Check if we're actually in the space
    if (!this.isConnected) {
      console.log('AblySpacesService: Not connected, location will be restored on reconnection');
      return;
    }

    // Create location key for duplicate detection
    const locationKey = `${location.page}:${location.subLocation || ''}:${location.sectionId || ''}`;

    // Skip if same location (duplicate detection)
    if (this.lastPublishedLocation === locationKey) {
      return;
    }

    // Cancel any pending location update (debounce)
    if (this.pendingLocationUpdate) {
      clearTimeout(this.pendingLocationUpdate.timeoutId);
    }

    // Schedule the update with debouncing
    const timeoutId = setTimeout(async () => {
      this.pendingLocationUpdate = null;

      // Double-check we're still connected
      if (!this.space || !this.isConnected) {
        console.log('AblySpacesService: Lost connection during location update');
        return;
      }

      // Double-check location hasn't changed during debounce
      const currentKey = `${this.currentLocation?.page}:${this.currentLocation?.subLocation || ''}:${this.currentLocation?.sectionId || ''}`;
      if (currentKey !== locationKey) {
        return;
      }

      try {
        await this.space.locations.set(location);
        this.lastPublishedLocation = locationKey;
      } catch (error: any) {
        // Handle "must enter a space" error by triggering reconnection
        if (error?.message?.includes('must enter a space') || error?.code === 101001) {
          console.warn('AblySpacesService: Not in space, attempting to re-enter...', error);
          this.handleReconnection();
        } else {
          console.error('AblySpacesService: Failed to set location', error);
        }
      }
    }, this.locationDebounceMs);

    this.pendingLocationUpdate = { location, timeoutId };
  }

  /**
   * Get current location
   */
  getCurrentLocation(): UserLocation | null {
    return this.currentLocation;
  }

  /**
   * Set sub-location (for drawers, modals, etc.)
   * This updates the current location's subLocation without changing the page
   */
  async setSubLocation(subLocation: string | null): Promise<void> {
    if (!this.currentLocation) {
      console.warn('AblySpacesService: Cannot set sub-location without a page location');
      return;
    }

    const newLocation: UserLocation = {
      ...this.currentLocation,
      subLocation: subLocation || undefined,
    };

    await this.setLocation(newLocation);
  }

  /**
   * Clear sub-location (when closing a drawer/modal)
   */
  async clearSubLocation(): Promise<void> {
    await this.setSubLocation(null);
  }

  /**
   * Set question ID for quiz question-level presence tracking.
   * This updates the current location's questionId without changing other fields.
   */
  async setQuestionId(questionId: string | null): Promise<void> {
    if (!this.currentLocation) {
      console.warn('AblySpacesService: Cannot set questionId without a page location');
      return;
    }

    const newLocation: UserLocation = {
      ...this.currentLocation,
      questionId: questionId || undefined,
    };

    await this.setLocation(newLocation);
  }

  /**
   * Clear question ID (when deselecting a question)
   */
  async clearQuestionId(): Promise<void> {
    await this.setQuestionId(null);
  }

  /**
   * Get collaborators viewing a specific question
   */
  getCollaboratorsOnQuestion(questionId: string): Collaborator[] {
    return this.collaboratorsSubject.value.filter(collaborator => {
      if (collaborator.isCurrentUser) return false;
      return collaborator.location?.questionId === questionId;
    });
  }

  /**
   * Get collaborators on the same page/location
   * Only returns collaborators who are on the same page
   */
  getCollaboratorsOnSamePage(): Collaborator[] {
    if (!this.currentLocation) return this.collaboratorsSubject.value;

    return this.collaboratorsSubject.value.filter(collaborator => {
      // Always include current user
      if (collaborator.isCurrentUser) return true;
      // Include collaborators on the same page
      return collaborator.location?.page === this.currentLocation?.page;
    });
  }

  /**
   * Observable of collaborators on the same page
   */
  get collaboratorsOnSamePage$(): Observable<Collaborator[]> {
    return this.collaborators$.pipe(
      map(collaborators => {
        if (!this.currentLocation) return collaborators;
        return collaborators.filter(c =>
          c.isCurrentUser || c.location?.page === this.currentLocation?.page
        );
      })
    );
  }

  /**
   * Get current user info
   */
  getCurrentUser(): AblyTokenResponse['user'] | null {
    return this.currentUser;
  }

  /**
   * Check if connected
   */
  getIsConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Check if cursors are paused (due to tab being hidden)
   */
  areCursorsPaused(): boolean {
    return this.cursorsPaused;
  }

  // ============= Tab Visibility Cost Optimization =============
  // Pauses cursors immediately when tab hidden, disconnects after 6 minutes

  /**
   * Set up handler for tab visibility changes
   * - Immediately pauses cursors when tab is hidden
   * - Disconnects after 6 minutes of being hidden
   * - Auto-reconnects when tab becomes visible
   */
  private setupVisibilityChangeHandler(projectId: string): void {
    if (!this.isBrowser) return;

    // Remove existing handler if any
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        this.handleTabHidden(projectId);
      } else {
        this.handleTabVisible(projectId);
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);

    // Set initial state
    this.isTabVisible = !document.hidden;
  }

  /**
   * Handle tab becoming hidden
   * - Immediately pause cursors (stop SENDING, but still RECEIVE updates)
   * - Schedule disconnect after extended idle period (cost optimization)
   */
  private handleTabHidden(projectId: string): void {
    console.log('AblySpacesService: Tab hidden - pausing cursor sending');
    this.isTabVisible = false;

    // Immediately pause cursors (stop sending, but connection stays alive for receiving)
    this.cursorsPaused = true;
    this.cursorsPausedSubject.next(true);

    // Clear cursor position to hide from others
    this.clearCursor();

    // Schedule disconnect after extended idle period (cost optimization)
    // This saves Ably connection minutes when user is away for a long time
    this.scheduleIdleDisconnect(projectId);
  }

  /**
   * Schedule disconnect after extended idle period
   * Saves Ably costs when user is away for a long time
   */
  private scheduleIdleDisconnect(projectId: string): void {
    // Clear any existing timeout
    if (this.idleDisconnectTimeoutId) {
      clearTimeout(this.idleDisconnectTimeoutId);
    }

    console.log(`AblySpacesService: Scheduling idle disconnect in ${this.idleDisconnectMs / 1000 / 60} minutes`);

    this.idleDisconnectTimeoutId = setTimeout(async () => {
      // Double-check tab is still hidden
      if (!this.isTabVisible && this.isConnected) {
        console.log('AblySpacesService: Idle timeout reached, disconnecting to save costs');
        this.wasIdleDisconnected = true;
        await this.disconnect();
      }
    }, this.idleDisconnectMs);
  }

  /**
   * Cancel scheduled idle disconnect
   */
  private cancelIdleDisconnect(): void {
    if (this.idleDisconnectTimeoutId) {
      clearTimeout(this.idleDisconnectTimeoutId);
      this.idleDisconnectTimeoutId = null;
    }
  }

  /**
   * Handle tab becoming visible
   * - Resume cursors
   * - Reconnect if we were idle-disconnected
   * - Emit event so components can refresh data from backend
   */
  private async handleTabVisible(projectId: string): Promise<void> {
    console.log('AblySpacesService: Tab visible - resuming');
    this.isTabVisible = true;

    // Cancel any pending idle disconnect
    this.cancelIdleDisconnect();

    // Resume cursors
    this.cursorsPaused = false;
    this.cursorsPausedSubject.next(false);

    // If we were idle-disconnected, reconnect
    if (this.wasIdleDisconnected && !this.isConnected) {
      console.log('AblySpacesService: Reconnecting after idle disconnect');
      this.wasIdleDisconnected = false;
      await this.connect(projectId);
    }

    // Emit tab visible event so components can refresh data from backend if needed
    this.tabBecameVisibleSubject.next(true);
  }

  /**
   * Clean up visibility handler
   */
  private cleanupVisibilityHandler(): void {
    if (this.isBrowser && this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
    // Also cancel any pending idle disconnect
    this.cancelIdleDisconnect();
  }

  // ============= Real-time Field Value Sync =============
  // Cost Optimization: Debounced publishing with duplicate detection

  private fieldValueCallbacks = new Map<string, (value: string, userId: string) => void>();
  private pendingFieldUpdates = new Map<string, { value: string; timeoutId: any }>();
  private lastPublishedValues = new Map<string, string>(); // Track last published values to avoid duplicates
  private fieldValueDebounceMs = 300; // Debounce field value updates (was 100ms throttle)
  private fieldValuesChannel: any = null; // Store channel reference for cleanup

  /**
   * Get the channel name for field values (uses space name for proper capabilities)
   */
  private getFieldValuesChannelName(): string {
    // Use space name as prefix to match token capabilities: {space_name}:*
    return `${this.currentSpaceName}:field-values`;
  }

  /**
   * Publish a field value change to other collaborators (debounced with duplicate detection)
   * Cost optimization: Only publishes after user stops typing for 300ms, and skips if value unchanged
   */
  async publishFieldValue(fieldId: string, value: string): Promise<void> {
    if (!this.client || !this.currentSpaceName) {
      return;
    }

    // Cancel any pending update for this field (debounce)
    const pending = this.pendingFieldUpdates.get(fieldId);
    if (pending) {
      clearTimeout(pending.timeoutId);
    }

    // Schedule the update with debouncing
    const timeoutId = setTimeout(async () => {
      this.pendingFieldUpdates.delete(fieldId);

      // Skip if value hasn't changed since last publish (duplicate detection)
      const lastValue = this.lastPublishedValues.get(fieldId);
      if (lastValue === value) {
        return;
      }

      // Check we're still connected before publishing
      if (!this.client || !this.isConnected) {
        return;
      }

      try {
        const channelName = this.getFieldValuesChannelName();
        const channel = this.client.channels.get(channelName);
        await channel.publish('value-change', {
          fieldId,
          value,
          userId: this.currentUser?.id,
          userName: this.currentUser?.name,
          connectionId: this.client.connection.id, // Unique per tab - used to filter own messages
          timestamp: Date.now(),
        });
        // Update last published value
        this.lastPublishedValues.set(fieldId, value);
      } catch (error) {
        console.error('AblySpacesService: Failed to publish field value', error);
      }
    }, this.fieldValueDebounceMs);

    this.pendingFieldUpdates.set(fieldId, { value, timeoutId });
  }

  /**
   * Subscribe to field value changes from other collaborators
   * @returns Unsubscribe function to remove this specific callback
   */
  subscribeToFieldValues(callback: (fieldId: string, value: string, userId: string, userName: string) => void): () => void {
    if (!this.client || !this.currentSpaceName) {
      console.log('AblySpacesService: Cannot subscribe - not connected', { client: !!this.client, spaceName: this.currentSpaceName });
      return () => {}; // Return no-op if not connected
    }

    try {
      const channelName = this.getFieldValuesChannelName();
      console.log('AblySpacesService: Subscribing to field values on channel', channelName);
      this.fieldValuesChannel = this.client.channels.get(channelName);

      // Store the wrapped callback so we can unsubscribe it later
      const wrappedCallback = (message: any) => {
        // Guard against messages received after disconnect
        if (!this.isConnected) {
          return;
        }
        const { fieldId, value, userId, userName, connectionId } = message.data;
        // Don't process messages from THIS tab (same connectionId)
        // But DO process messages from same user in DIFFERENT tab (different connectionId)
        if (connectionId !== this.client?.connection?.id) {
          callback(fieldId, value, userId, userName);
        }
      };

      this.fieldValuesChannel.subscribe('value-change', wrappedCallback);
      console.log('AblySpacesService: Subscribed to field values successfully');

      // Return unsubscribe function for this specific callback
      return () => {
        if (this.fieldValuesChannel) {
          try {
            this.fieldValuesChannel.unsubscribe('value-change', wrappedCallback);
            console.log('AblySpacesService: Unsubscribed specific field values callback');
          } catch (error) {
            console.error('AblySpacesService: Failed to unsubscribe field values callback', error);
          }
        }
      };
    } catch (error) {
      console.error('AblySpacesService: Failed to subscribe to field values', error);
      return () => {}; // Return no-op on error
    }
  }

  /**
   * Unsubscribe from field value changes
   */
  unsubscribeFromFieldValues(): void {
    if (!this.fieldValuesChannel) return;

    try {
      this.fieldValuesChannel.unsubscribe();
      this.fieldValuesChannel.detach();
      this.fieldValuesChannel = null;
    } catch (error) {
      console.error('AblySpacesService: Failed to unsubscribe from field values', error);
    }
  }

  // ============= Real-time List Changes Sync =============

  private listChangesChannel: any = null;

  // ============= Real-time Drag Events Sync =============
  // For curriculum drag & drop position updates with animated UI

  private dragEventsChannel: any = null;
  // Use ReplaySubject(1) to ensure late subscribers get the latest event
  // This fixes edge cases where the signal subscription happens slightly after event emission
  private dragEventSubject = new ReplaySubject<DragEvent>(1);
  dragEvents$ = this.dragEventSubject.asObservable();

  /**
   * Get the channel name for list changes
   */
  private getListChangesChannelName(): string {
    return `${this.currentSpaceName}:list-changes`;
  }

  /**
   * Publish a list item change (add/remove) to other collaborators
   */
  async publishListChange(
    changeType: 'add' | 'remove',
    listType: string,
    item: any
  ): Promise<void> {
    if (!this.client || !this.currentSpaceName) {
      console.log('AblySpacesService: Cannot publish list change - not connected');
      return;
    }

    try {
      const channelName = this.getListChangesChannelName();
      const channel = this.client.channels.get(channelName);
      await channel.publish('list-change', {
        changeType,
        listType,
        item,
        userId: this.currentUser?.id,
        userName: this.currentUser?.name,
        connectionId: this.client.connection.id, // Unique per tab
        timestamp: Date.now(),
      });
    } catch (error) {
      console.error('AblySpacesService: Failed to publish list change', error);
    }
  }

  /**
   * Subscribe to list changes from other collaborators
   * @returns Unsubscribe function to remove this specific callback
   */
  subscribeToListChanges(
    callback: (changeType: 'add' | 'remove', listType: string, item: any, userId: number, userName: string) => void
  ): () => void {
    if (!this.client || !this.currentSpaceName) {
      console.log('AblySpacesService: Cannot subscribe to list changes - not connected');
      return () => {}; // Return no-op if not connected
    }

    try {
      const channelName = this.getListChangesChannelName();
      console.log('AblySpacesService: Subscribing to list changes on channel', channelName);
      this.listChangesChannel = this.client.channels.get(channelName);

      // Store the wrapped callback so we can unsubscribe it later
      const wrappedCallback = (message: any) => {
        // Guard against messages received after disconnect
        if (!this.isConnected) {
          return;
        }
        const { changeType, listType, item, userId, userName, connectionId } = message.data;
        // Don't process messages from THIS tab (same connectionId)
        // But DO process messages from same user in DIFFERENT tab (different connectionId)
        if (connectionId !== this.client?.connection?.id) {
          callback(changeType, listType, item, userId, userName);
        }
      };

      this.listChangesChannel.subscribe('list-change', wrappedCallback);
      console.log('AblySpacesService: Subscribed to list changes successfully');

      // Return unsubscribe function for this specific callback
      return () => {
        if (this.listChangesChannel) {
          try {
            this.listChangesChannel.unsubscribe('list-change', wrappedCallback);
            console.log('AblySpacesService: Unsubscribed specific list changes callback');
          } catch (error) {
            console.error('AblySpacesService: Failed to unsubscribe list changes callback', error);
          }
        }
      };
    } catch (error) {
      console.error('AblySpacesService: Failed to subscribe to list changes', error);
      return () => {}; // Return no-op on error
    }
  }

  /**
   * Unsubscribe from list changes
   */
  unsubscribeFromListChanges(): void {
    if (!this.listChangesChannel) return;

    try {
      this.listChangesChannel.unsubscribe();
      this.listChangesChannel.detach();
      this.listChangesChannel = null;
    } catch (error) {
      console.error('AblySpacesService: Failed to unsubscribe from list changes', error);
    }
  }

  // ============= Real-time Drag Events =============
  // For curriculum drag & drop with animated ghost items

  /**
   * Get the channel name for drag events
   */
  private getDragEventsChannelName(): string {
    return `${this.currentSpaceName}:drag-events`;
  }

  /**
   * Publish a drag event (start, move, or end)
   */
  async publishDragEvent(
    eventType: DragEventType,
    item: DragEventItem,
    sourceIndex: number,
    currentIndex: number,
    sourceSectionId?: string,
    targetSectionId?: string,
    position?: { x: number; y: number }
  ): Promise<void> {
    // Only log for start/end, not move (too frequent)
    if (eventType !== 'drag_move') {
      console.log('AblySpacesService: publishDragEvent called', {
        eventType,
        item,
        client: !!this.client,
        spaceName: this.currentSpaceName,
        user: this.currentUser?.name
      });
    }

    if (!this.client || !this.currentSpaceName || !this.currentUser) {
      if (eventType !== 'drag_move') {
        console.warn('AblySpacesService: Cannot publish drag event - not connected');
      }
      return;
    }

    try {
      const channelName = this.getDragEventsChannelName();
      const channel = this.client.channels.get(channelName);
      await channel.publish('drag-event', {
        type: eventType,
        item,
        sourceIndex,
        currentIndex,
        sourceSectionId,
        targetSectionId,
        position, // Include position for real-time movement tracking
        userId: this.currentUser.id,
        userName: this.currentUser.name,
        userColor: this.currentUser.color,
        connectionId: this.client.connection.id,
        timestamp: Date.now(),
      });
      if (eventType !== 'drag_move') {
        console.log('AblySpacesService: Drag event published successfully');
      }
    } catch (error) {
      console.error('AblySpacesService: Failed to publish drag event', error);
    }
  }

  // Subject for positions updated events (separate from drag events)
  // Use ReplaySubject(1) to ensure late subscribers get the latest event
  private positionsUpdatedSubject = new ReplaySubject<{ sectionId: string; positions: Array<{ subsection: string; position: number }>; userName: string }>(1);
  positionsUpdated$ = this.positionsUpdatedSubject.asObservable();

  /**
   * Subscribe to drag events from other collaborators
   */
  subscribeToDragEvents(): void {
    if (!this.client || !this.currentSpaceName) {
      console.log('AblySpacesService: Cannot subscribe to drag events - not connected');
      return;
    }

    try {
      const channelName = this.getDragEventsChannelName();
      console.log('AblySpacesService: Subscribing to drag events on channel', channelName);
      this.dragEventsChannel = this.client.channels.get(channelName);

      // Subscribe to drag events (start, move, end)
      this.dragEventsChannel.subscribe('drag-event', (message: any) => {
        // Guard against messages received after disconnect
        if (!this.isConnected) {
          return;
        }
        const { connectionId, ...eventData } = message.data;
        const isOwnEvent = connectionId === this.client?.connection?.id;
        // Don't process messages from THIS tab
        if (!isOwnEvent) {
          this.dragEventSubject.next(eventData as DragEvent);
        }
      });

      // Subscribe to positions-updated events (after API success) - for subsections
      this.dragEventsChannel.subscribe('positions-updated', (message: any) => {
        if (!this.isConnected) {
          return;
        }
        const { connectionId, sectionId, positions, userName } = message.data;
        // Don't process messages from THIS tab
        if (connectionId !== this.client?.connection?.id) {
          console.log('AblySpacesService: Received positions-updated event', { sectionId, positions });
          this.positionsUpdatedSubject.next({ sectionId, positions, userName });
        }
      });

      // Subscribe to section-positions-updated events (after API success) - for sections
      this.dragEventsChannel.subscribe('section-positions-updated', (message: any) => {
        if (!this.isConnected) {
          return;
        }
        const { connectionId, courseId, positions, userName } = message.data;
        // Don't process messages from THIS tab
        if (connectionId !== this.client?.connection?.id) {
          console.log('AblySpacesService: Received section-positions-updated event', { courseId, positions });
          this.sectionPositionsUpdatedSubject.next({ courseId, positions, userName });
        }
      });

      console.log('AblySpacesService: Subscribed to drag events successfully');
    } catch (error) {
      console.error('AblySpacesService: Failed to subscribe to drag events', error);
    }
  }

  /**
   * Unsubscribe from drag events
   */
  unsubscribeFromDragEvents(): void {
    if (!this.dragEventsChannel) {
      return;
    }

    const channel = this.dragEventsChannel;
    this.dragEventsChannel = null;

    try {
      channel.unsubscribe();
    } catch (error) {
      console.error('AblySpacesService: Failed to unsubscribe from drag events', error);
    }

    // detach() returns a Promise — catch rejection when channel is still attaching
    channel.detach().catch((error: unknown) => {
      console.warn('AblySpacesService: Channel detach warning (safe to ignore during teardown)', error);
    });
  }

  /**
   * Publish positions updated event after successful API call
   * This provides immediate feedback to remote users; SSE serves as backup
   */
  async publishPositionsUpdated(
    sectionId: string,
    positions: Array<{ subsection: string; position: number }>
  ): Promise<void> {
    console.log('AblySpacesService: publishPositionsUpdated called', { sectionId, positions });

    if (!this.client || !this.currentSpaceName || !this.currentUser) {
      console.warn('AblySpacesService: Cannot publish positions update - not connected');
      return;
    }

    try {
      const channelName = this.getDragEventsChannelName();
      const channel = this.client.channels.get(channelName);
      await channel.publish('positions-updated', {
        sectionId,
        positions,
        userId: this.currentUser.id,
        userName: this.currentUser.name,
        userColor: this.currentUser.color,
        connectionId: this.client.connection.id,
        timestamp: Date.now(),
      });
      console.log('AblySpacesService: Positions updated event published successfully');
    } catch (error) {
      console.error('AblySpacesService: Failed to publish positions update', error);
    }
  }

  // Subject for section positions updated events
  // Use ReplaySubject(1) to ensure late subscribers get the latest event
  private sectionPositionsUpdatedSubject = new ReplaySubject<{ courseId: string; positions: Array<{ section: string; position: number }>; userName: string }>(1);
  sectionPositionsUpdated$ = this.sectionPositionsUpdatedSubject.asObservable();

  /**
   * Publish section positions updated event after successful API call
   * This provides immediate feedback to remote users; SSE serves as backup
   */
  async publishSectionPositionsUpdated(
    courseId: string,
    positions: Array<{ section: string; position: number }>
  ): Promise<void> {
    console.log('AblySpacesService: publishSectionPositionsUpdated called', { courseId, positions });

    if (!this.client || !this.currentSpaceName || !this.currentUser) {
      console.warn('AblySpacesService: Cannot publish section positions update - not connected');
      return;
    }

    try {
      const channelName = this.getDragEventsChannelName();
      const channel = this.client.channels.get(channelName);
      await channel.publish('section-positions-updated', {
        courseId,
        positions,
        userId: this.currentUser.id,
        userName: this.currentUser.name,
        userColor: this.currentUser.color,
        connectionId: this.client.connection.id,
        timestamp: Date.now(),
      });
      console.log('AblySpacesService: Section positions updated event published successfully');
    } catch (error) {
      console.error('AblySpacesService: Failed to publish section positions update', error);
    }
  }

  // ============================================================================
  // VIDEO UPLOAD EVENTS VIA ABLY - DISABLED (using SSE instead for cost savings)
  // ============================================================================
  // The code below is commented out but preserved for future use.
  // Currently, video upload events are received via ProjectEventsSSE instead.
  //
  // To re-enable Ably for video upload events:
  // 1. Uncomment videoUploadChannel and the methods below
  // 2. Uncomment videoUploadEventSubject at top of class
  // 3. Uncomment subscribeToVideoUploadEvents() call in connect() method
  // 4. Update CollaborationFacade to expose videoUploadEvents$
  // 5. Update course-detail.component.ts to use collaboration.videoUploadEvents$
  //
  // private videoUploadChannel: any = null;
  //
  // /**
  //  * Subscribe to video upload events from other collaborators.
  //  * Called automatically when connected to a space.
  //  * Events: promo_video_upload_initiated, promo_video_upload_completed,
  //  *         lecture_video_upload_initiated, lecture_video_upload_completed
  //  */
  // subscribeToVideoUploadEvents(): void {
  //   if (!this.client || !this.currentSpaceName) {
  //     console.log('AblySpacesService: Cannot subscribe to video uploads - not connected');
  //     return;
  //   }
  //
  //   try {
  //     const channelName = this.currentSpaceName;
  //     console.log('AblySpacesService: Subscribing to video upload events on', channelName);
  //
  //     this.videoUploadChannel = this.client.channels.get(channelName);
  //
  //     const videoEventTypes = [
  //       'promo_video_upload_initiated',
  //       'promo_video_upload_completed',
  //       'lecture_video_upload_initiated',
  //       'lecture_video_upload_completed'
  //     ];
  //
  //     videoEventTypes.forEach(eventType => {
  //       this.videoUploadChannel.subscribe(eventType, (message: any) => {
  //         if (!this.isConnected) return;
  //
  //         const data = message.data;
  //         if (data.user_id === this.currentUser?.id) {
  //           console.log('AblySpacesService: Ignoring own video upload event', eventType);
  //           return;
  //         }
  //
  //         console.log('AblySpacesService: Received video upload event', eventType, data);
  //
  //         const event: VideoUploadEvent = {
  //           type: eventType as VideoUploadEvent['type'],
  //           library_id: data.data?.library_id || data.library_id,
  //           course_id: data.data?.course_id || data.course_id,
  //           lecture_id: data.data?.lecture_id || data.lecture_id,
  //           content_type: data.data?.content_type || data.content_type,
  //           filename: data.data?.filename || data.filename,
  //           status: data.data?.status || data.status,
  //           size: data.data?.size || data.size,
  //           user_id: data.user_id,
  //           user_name: data.user_name || 'Unknown',
  //           user_color: data.user_color,
  //           timestamp: data.timestamp || Date.now(),
  //         };
  //
  //         this.videoUploadEventSubject.next(event);
  //       });
  //     });
  //
  //     console.log('AblySpacesService: Subscribed to video upload events successfully');
  //   } catch (error) {
  //     console.error('AblySpacesService: Failed to subscribe to video upload events', error);
  //   }
  // }
  //
  // /**
  //  * Unsubscribe from video upload events
  //  */
  // unsubscribeFromVideoUploadEvents(): void {
  //   if (!this.videoUploadChannel) return;
  //
  //   try {
  //     this.videoUploadChannel.unsubscribe('promo_video_upload_initiated');
  //     this.videoUploadChannel.unsubscribe('promo_video_upload_completed');
  //     this.videoUploadChannel.unsubscribe('lecture_video_upload_initiated');
  //     this.videoUploadChannel.unsubscribe('lecture_video_upload_completed');
  //     this.videoUploadChannel = null;
  //     console.log('AblySpacesService: Unsubscribed from video upload events');
  //   } catch (error) {
  //     console.error('AblySpacesService: Failed to unsubscribe from video upload events', error);
  //   }
  // }
  // ============================================================================

  // Private methods

  private getToken(projectId: string): Observable<AblyTokenResponse | null> {
    return this.http.post<AblyTokenResponse>('/api/v2/courses/ably/auth/token/', { project_id: projectId }).pipe(
      catchError((error) => {
        console.error('AblySpacesService: Token request failed', error);
        return of(null);
      })
    );
  }

  private async setupMemberSubscriptions(): Promise<void> {
    if (!this.space) return;

    // IMPORTANT: Get initial members FIRST, then subscribe to updates
    // This prevents race condition where late-arriving getAll() overwrites updates
    try {
      const members = await this.space.members.getAll();
      console.log('AblySpacesService: Initial members loaded:', members.length);
      this.updateCollaborators(members);
      // Fetch initial locations for all members
      await this.refreshAllLocations();
    } catch (error) {
      console.error('AblySpacesService: Failed to get initial members', error);
    }

    // Now subscribe to member events (after initial load)
    const refreshMembers = async (event: any) => {
      // Guard against calls after disconnect
      if (!this.space || !this.isConnected) return;

      console.log('AblySpacesService: Member event', event?.action, event?.clientId);

      const members = await this.space.members.getAll();
      console.log('AblySpacesService: Members updated', members.length, members.map((m: any) => m.clientId));
      this.updateCollaborators(members);
      // Re-fetch locations after member changes to ensure data consistency
      await this.refreshAllLocations();
    };

    this.space.members.subscribe('enter', refreshMembers);
    this.space.members.subscribe('leave', refreshMembers);
    this.space.members.subscribe('remove', refreshMembers);
    this.space.members.subscribe('update', refreshMembers);
  }

  private async setupLockSubscriptions(): Promise<void> {
    if (!this.space) return;

    // IMPORTANT: Get initial locks FIRST, then subscribe to updates
    // This prevents race condition where late-arriving getAll() overwrites
    // incremental updates from subscribe()
    try {
      const locks = await this.space.locks.getAll();
      this.updateLocks(locks);
      console.log('AblySpacesService: Initial locks loaded:', locks.length);
    } catch (error) {
      console.error('AblySpacesService: Failed to get initial locks', error);
    }

    // Now subscribe to lock updates (after initial load)
    this.space.locks.subscribe('update', (lock: any) => {
      this.handleLockUpdate(lock);
    });
  }

  private setupCursorSubscriptions(): void {
    if (!this.space) return;

    // Subscribe to cursor updates
    this.space.cursors.subscribe('update', (cursorUpdate: any) => {
      this.handleCursorUpdate(cursorUpdate);
    });
  }

  private async setupLocationSubscriptions(): Promise<void> {
    if (!this.space) return;

    // IMPORTANT: Get initial locations FIRST, then subscribe to updates
    // This prevents race condition where late-arriving getAll() overwrites updates
    try {
      const locations = await this.space.locations.getAll();
      console.log('AblySpacesService: Initial locations loaded:', Object.keys(locations).length);
      // Update all collaborators with their locations
      Object.entries(locations).forEach(([connectionId, location]: [string, any]) => {
        this.updateCollaboratorLocation(connectionId, location as UserLocation);
      });
    } catch (error) {
      console.error('AblySpacesService: Failed to get initial locations', error);
    }

    // Now subscribe to location updates (after initial load)
    this.space.locations.subscribe('update', (locationUpdate: any) => {
      this.handleLocationUpdate(locationUpdate);
    });

    // If we have a pending location, set it now
    if (this.currentLocation) {
      this.space.locations.set(this.currentLocation);
    }
  }

  private handleLocationUpdate(locationUpdate: any): void {
    // Guard against calls after disconnect
    if (!this.space || !this.isConnected) return;

    console.log('AblySpacesService: Location update', locationUpdate);

    const { member, currentLocation } = locationUpdate;
    if (member?.connectionId) {
      this.updateCollaboratorLocation(member.connectionId, currentLocation);
    }
  }

  private updateCollaboratorLocation(connectionId: string, location: UserLocation): void {
    const collaborators = [...this.collaboratorsSubject.value];
    const index = collaborators.findIndex(c => c.connectionId === connectionId);

    if (index !== -1) {
      collaborators[index] = { ...collaborators[index], location };
      this.collaboratorsSubject.next(collaborators);
    }
  }

  /**
   * Refresh all collaborator locations from Ably
   * Called after member changes to ensure location data is preserved
   */
  private async refreshAllLocations(): Promise<void> {
    if (!this.space || !this.isConnected) return;

    try {
      const locations = await this.space.locations.getAll();
      const collaborators = [...this.collaboratorsSubject.value];
      let hasChanges = false;

      Object.entries(locations).forEach(([connectionId, location]: [string, any]) => {
        const index = collaborators.findIndex(c => c.connectionId === connectionId);
        if (index !== -1 && location) {
          collaborators[index] = { ...collaborators[index], location: location as UserLocation };
          hasChanges = true;
        }
      });

      if (hasChanges) {
        this.collaboratorsSubject.next(collaborators);
      }
    } catch (error) {
      console.warn('AblySpacesService: Failed to refresh locations', error);
    }
  }

  private updateCollaborators(members: any[]): void {
    // Deduplicate members by clientId (user ID) - keep only the LATEST connection per user
    // This handles reconnection scenarios where a user may briefly have multiple connections
    const membersByClientId = new Map<string, any>();

    // Sort by connectionId descending to process latest connections first
    const sortedMembers = [...members].sort((a, b) => {
      // connectionId is a string, use localeCompare for proper comparison
      return (b.connectionId || '').localeCompare(a.connectionId || '');
    });

    sortedMembers.forEach((member) => {
      const existingMember = membersByClientId.get(member.clientId);

      // Only keep the member if:
      // 1. We don't have this user yet, OR
      // 2. This connection is newer (higher connectionId)
      if (!existingMember) {
        membersByClientId.set(member.clientId, member);
      }
    });

    // Get existing collaborators to preserve their locations and cursor positions
    const existingCollaborators = this.collaboratorsSubject.value;
    const existingByConnectionId = new Map(
      existingCollaborators.map(c => [c.connectionId, c])
    );
    // Also index by user ID for fallback when connectionId changes (reconnection scenarios)
    const existingByUserId = new Map(
      existingCollaborators.map(c => [c.id, c])
    );

    const collaborators: Collaborator[] = Array.from(membersByClientId.values()).map((member) => {
      const userId = member.profileData?.id || (member.clientId || '').replace('user-', '');

      // Try to find existing collaborator to preserve location/cursor data
      // First try by connectionId (most common case)
      let existing = existingByConnectionId.get(member.connectionId);

      // CRITICAL FIX: If not found by connectionId, try by user ID
      // This preserves cursor data when connectionId changes during reconnection
      if (!existing) {
        existing = existingByUserId.get(userId);
      }

      return {
        id: userId,
        name: member.profileData?.name || 'Unknown',
        email: member.profileData?.email || '',
        avatar: member.profileData?.avatar,
        color: member.profileData?.color || '#6366f1',
        isOwner: member.profileData?.isOwner || false,
        connectionId: member.connectionId,
        cursorPosition: existing?.cursorPosition, // Preserve cursor position
        location: existing?.location, // Preserve location
        lockedComponents: existing?.lockedComponents || [],
        isCurrentUser: member.clientId === `user-${this.currentUser?.id}`,
      };
    });

    console.log('AblySpacesService: Collaborators updated', collaborators.length,
      'unique users:', membersByClientId.size);
    this.collaboratorsSubject.next(collaborators);
  }

  private updateLocks(locks: any[]): void {
    const lockMap = new Map<string, LockInfo>();

    locks.forEach((lock) => {
      if (lock.status === 'locked' && lock.member) {
        lockMap.set(lock.id, {
          componentId: lock.id,
          memberId: lock.member.clientId || 'unknown',
          memberName: lock.member.profileData?.name || 'Unknown',
          memberColor: lock.member.profileData?.color || '#6366f1',
          lockedAt: new Date(),
          isLockedByMe: lock.member.clientId === `user-${this.currentUser?.id}`,
        });
      }
    });

    this.locksSubject.next(lockMap);
  }

  private handleLockUpdate(lock: any): void {
    const currentLocks = new Map(this.locksSubject.value);

    if (lock.status === 'locked' && lock.member) {
      currentLocks.set(lock.id, {
        componentId: lock.id,
        memberId: lock.member.clientId || 'unknown',
        memberName: lock.member.profileData?.name || 'Unknown',
        memberColor: lock.member.profileData?.color || '#6366f1',
        lockedAt: new Date(),
        isLockedByMe: lock.member.clientId === `user-${this.currentUser?.id}`,
      });
    } else {
      currentLocks.delete(lock.id);
    }

    this.locksSubject.next(currentLocks);
  }

  private handleCursorUpdate(cursorUpdate: any): void {
    // Update cursor position for collaborator
    const collaborators = [...this.collaboratorsSubject.value];

    // Try to find by connectionId first (most common case)
    let index = collaborators.findIndex((c) => c.connectionId === cursorUpdate.connectionId);

    // CRITICAL FIX: If not found by connectionId, try finding by member clientId
    // This handles race conditions and reconnection scenarios where connectionId may be stale
    if (index === -1 && cursorUpdate.member?.clientId) {
      const memberClientId = cursorUpdate.member.clientId;
      const memberUserId = cursorUpdate.member.profileData?.id;

      index = collaborators.findIndex((c) => {
        // Match by Ably clientId (format: "user-{userId}")
        const collaboratorClientId = `user-${c.id}`;
        if (collaboratorClientId === memberClientId) return true;

        // Also try matching by raw user ID from profile data
        if (memberUserId && c.id === memberUserId) return true;

        return false;
      });

      // If we found the collaborator by clientId, update their connectionId to current one
      // This fixes the stale connectionId issue for future updates
      if (index !== -1) {
        console.log('AblySpacesService: Found collaborator by clientId, updating connectionId',
          { old: collaborators[index].connectionId, new: cursorUpdate.connectionId });
        collaborators[index].connectionId = cursorUpdate.connectionId;
      }
    }

    // Only proceed if we found the collaborator
    if (index === -1) {
      console.warn('AblySpacesService: Cursor update for unknown collaborator', {
        connectionId: cursorUpdate.connectionId,
        clientId: cursorUpdate.member?.clientId,
        availableCollaborators: collaborators.map(c => ({
          id: c.id,
          name: c.name,
          connectionId: c.connectionId,
          isCurrentUser: c.isCurrentUser
        }))
      });
      return;
    }

    // Don't show hidden cursors
    if (cursorUpdate.data?.state === 'hidden') {
      collaborators[index].cursorPosition = undefined;
    } else {
      collaborators[index].cursorPosition = {
        x: cursorUpdate.position?.x || 0,
        y: cursorUpdate.position?.y || 0,
        elementId: cursorUpdate.data?.elementId,
        scope: cursorUpdate.data?.scope, // Store scope for context-aware display
      };
    }

    this.collaboratorsSubject.next(collaborators);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
